/**
 * recordingStorage.ts — IndexedDB storage for full recording Blobs
 *
 * Saves the complete raw recording to IndexedDB so it can be retried
 * later if transcription fails, or kept permanently for playback alongside
 * the transcript.
 *
 * Uses IndexedDB because Blobs can be large (100MB+) and localStorage
 * caps at ~5MB.
 */

const DB_NAME = 'whipt-recordings';
const STORE_NAME = 'recordings';
const DB_VERSION = 2;

export interface SavedRecording {
  id: string;
  blob: Blob;
  fileName: string;
  createdAt: string;
  duration: number; // seconds
  transcriptId?: string | number; // Links recording to transcript record
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('createdAt', 'createdAt', { unique: false });
        store.createIndex('transcriptId', 'transcriptId', { unique: false });
      }
      // If upgrading from v1 to v2, add the transcriptId index
      const oldVersion = event.oldVersion;
      if (oldVersion < 2) {
        const store = request.transaction?.objectStore(STORE_NAME);
        if (store && !store.indexNames.contains('transcriptId')) {
          store.createIndex('transcriptId', 'transcriptId', { unique: false });
        }
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/** Save a full recording Blob to IndexedDB */
export async function saveRecording(
  blob: Blob,
  fileName: string,
  duration: number,
  transcriptId?: string | number
): Promise<string> {
  const id = `rec_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.oncomplete = () => { db.close(); resolve(id); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.objectStore(STORE_NAME).put({
      id,
      blob,
      fileName,
      createdAt: new Date().toISOString(),
      duration,
      transcriptId,
    } satisfies SavedRecording);
  });
}

/** Update the transcriptId on an existing recording */
export async function updateRecordingTranscriptId(
  recordingId: string,
  transcriptId: string | number
): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(recordingId);
    getReq.onsuccess = () => {
      const rec = getReq.result as SavedRecording | undefined;
      if (rec) {
        rec.transcriptId = transcriptId;
        store.put(rec);
      }
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    };
    getReq.onerror = () => { db.close(); reject(getReq.error); };
  });
}

/** List all saved recordings (blobs excluded) */
export async function listSavedRecordings(): Promise<Omit<SavedRecording, 'blob'>[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      db.close();
      const all = request.result as SavedRecording[];
      resolve(all.map(({ blob, ...rest }) => rest));
    };
    request.onerror = () => { db.close(); reject(request.error); };
  });
}

/** Load a full recording Blob by id */
export async function loadRecording(id: string): Promise<SavedRecording | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).get(id);
    request.onsuccess = () => { db.close(); resolve(request.result ?? null); };
    request.onerror = () => { db.close(); reject(request.error); };
  });
}

/** Find a recording linked to a specific transcript ID */
export async function findRecordingByTranscriptId(
  transcriptId: string | number
): Promise<SavedRecording | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('transcriptId');
    const request = index.getAll(transcriptId);
    request.onsuccess = () => {
      db.close();
      const results = request.result as SavedRecording[];
      resolve(results.length > 0 ? results[0] : null);
    };
    request.onerror = () => { db.close(); reject(request.error); };
  });
}

/** Delete a saved recording by id */
export async function deleteRecording(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}